/* eslint-disable max-len */
import React from 'react';
import moment from 'moment';
import { useHistory } from 'react-router';

import PageUrls from 'Shared/constants/PageUrls.constants';

import { numberToCurrency } from 'Shared/helpers';
import { billingStates } from 'Shared/constants/constants';
import { Subscription } from 'Shared/constants/types';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpButton from 'Wpds/components/button/WpButton';
import { WpContainer, WpContainerBody, WpContainerHeader, WpContainerFooter } from 'Wpds/components/container';

type PlanInformationProps = {
  subscription?: Subscription;
  billingState: string;
  daysLeftInTrial: number;
};

const planSummary = ({ subscription, billingState, daysLeftInTrial }: PlanInformationProps) => {
  if (billingState === billingStates.FREE_TRIAL || billingState === billingStates.TRIAL_EXPIRED) {
    return (
      <div>
        <strong>You are on a free trial for the Growth Plan.</strong>
        <br />
        You&apos;ll have access to all of our premium features
        {typeof daysLeftInTrial === 'number' && daysLeftInTrial >= 0 && ` for ${daysLeftInTrial} days`}. Premium
        features will have a crown icon
        <span className="mx-2">
          <WpIcon name="crown" />
        </span>
        to let you know what they are. Happy exploring!
      </div>
    );
  }

  if (billingState === billingStates.FREE_PLAN) {
    return (
      <div>
        <strong>You are on the Basic Plan.</strong>
      </div>
    );
  }

  if (!subscription) {
    return null;
  }

  return (
    <div>
      You are on the <strong>{subscription.plan_name}</strong>, paying <strong>{subscription.billing_cycle}</strong>.
      Your plan will renew on <strong>{moment.unix(subscription.next_billing_date).format('MMMM Do, YYYY')}</strong> for{' '}
      <strong>{numberToCurrency(subscription.upcoming_invoice.subscription_amount / 100)}</strong>.{' '}
      {subscription.billing_cycle === 'monthly' && subscription.proration.amount !== 0 && (
        <>
          Adjusted for prorations, you will be billed{' '}
          <strong>{numberToCurrency(Math.abs(subscription.upcoming_invoice.amount) / 100)}</strong> on{' '}
          <strong>{moment.unix(subscription.next_billing_date).format('MMMM Do, YYYY')}</strong>.
        </>
      )}
      {subscription.billing_cycle === 'annually' && subscription.proration.amount > 0 && (
        <>
          Adjusted for prorations, you will be billed{' '}
          <strong>{numberToCurrency(Math.abs(subscription.proration.amount) / 100)}</strong> on{' '}
          <strong>{moment.unix(subscription.proration.due_on).format('MMMM Do, YYYY')}</strong>.
        </>
      )}
      {subscription.billing_cycle === 'annually' && subscription.proration.amount < 0 && (
        <>
          Adjusted for prorations, you will be credited{' '}
          <strong>{numberToCurrency(Math.abs(subscription.proration.amount) / 100)}</strong> on{' '}
          <strong>{moment.unix(subscription.next_billing_date).format('MMMM Do, YYYY')}</strong>.
        </>
      )}
    </div>
  );
};

const PlanInformation = ({ subscription, billingState, daysLeftInTrial }: PlanInformationProps) => {
  const history = useHistory();

  const handleViewPlansClick = () => history.push({ pathname: PageUrls.PLANS_PAGE, state: { modal: true } });

  return (
    <WpContainer>
      <WpContainerHeader>
        <h5>Subscription</h5>
      </WpContainerHeader>
      <WpContainerBody>{planSummary({ subscription, billingState, daysLeftInTrial })}</WpContainerBody>
      <WpContainerFooter>
        <WpButton qaTest="view-plans-button" onClick={handleViewPlansClick} variant="tertiary">
          View Plans
        </WpButton>
      </WpContainerFooter>
    </WpContainer>
  );
};

export default PlanInformation;
